import { useState, useMemo } from 'react'
import { Head, Link, usePage } from '@inertiajs/react'
import { usePublicPage } from '@/hooks/usePublicPage'
import { LandingNavbar } from '@/components/landing/LandingNavbar'
import { LandingFooter } from '@/components/landing/LandingFooter'
import {
  BookOpen,
  Clock,
  ArrowRight,
  Rss,
  Search,
  X,
  Sparkles,
  GraduationCap,
  Bell,
  Tag,
  Target
} from 'lucide-react'
import type { PageProps } from '@/types'

interface BlogCard {
  id: number
  title: string
  slug: string
  excerpt: string
  cover_image_url?: string
  author_name: string
  category: string
  exam_tags: string[]
  published_at: string
  read_minutes: number
}

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
}

interface Filters {
  category?: string
  tag?: string
}

interface BlogIndexProps {
  posts: BlogCard[]
  pagination: Pagination
  filters: Filters
}

const CATEGORY_LABELS: Record<string, string> = {
  announcement:       'Announcements',
  exam_guide:         'Exam Guide',
  preparation_tips:   'Prep Tips',
  general:            'General',
}

const CATEGORY_BADGES: Record<string, { bg: string, text: string, border: string }> = {
  announcement:       { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200' },
  exam_guide:         { bg: 'bg-sky-50', text: 'text-sky-800', border: 'border-sky-200' },
  preparation_tips:   { bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-200' },
  general:            { bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-200' },
}

function getCategoryIcon(cat: string) {
  switch (cat) {
    case 'announcement':
      return <Bell className="w-5 h-5" />
    case 'exam_guide':
      return <GraduationCap className="w-5 h-5" />
    case 'preparation_tips':
      return <Sparkles className="w-5 h-5" />
    default:
      return <BookOpen className="w-5 h-5" />
  }
}

export default function BlogIndex({ posts, pagination, filters }: BlogIndexProps) {
  const { auth } = usePage<PageProps>().props
  usePublicPage()
  const [selectedCategory, setSelectedCategory] = useState(filters.category || 'all')
  const [searchQuery, setSearchQuery] = useState('')

  const categories = ['all', 'announcement', 'exam_guide', 'preparation_tips', 'general']

  const handleCategoryChange = (cat: string) => {
    setSelectedCategory(cat)
    const url = cat === 'all' ? '/blog' : `/blog?category=${cat}`
    window.location.href = url
  }

  // Client-side search filtering across title, excerpt, and exam tags
  const filteredPosts = useMemo(() => {
    if (!searchQuery.trim()) return posts
    const query = searchQuery.toLowerCase().trim()
    return posts.filter(
      (p) =>
        p.title.toLowerCase().includes(query) ||
        p.excerpt.toLowerCase().includes(query) ||
        p.exam_tags.some((tag) => tag.toLowerCase().includes(query)) ||
        (CATEGORY_LABELS[p.category] || p.category).toLowerCase().includes(query)
    )
  }, [posts, searchQuery])

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'PrepKiya Blog',
    description: 'Exam preparation tips, announcements, and study guides for Indian competitive exams.',
    url: 'https://prepkiya.com/blog',
    publisher: {
      '@type': 'EducationalOrganization',
      name: 'PrepKiya',
      url: 'https://prepkiya.com'
    }
  }

  // Extract all unique exam tags for quick filtering
  const allExamTags = useMemo(() => {
    const tags = new Set<string>()
    posts.forEach((p) => p.exam_tags.forEach((t) => tags.add(t)))
    return Array.from(tags)
  }, [posts])

  return (
    <>
      <Head title="PrepKiya Blog — Exam Preparation Tips & Guides">
        <meta name="description" content="Read the PrepKiya blog for exam preparation tips, latest announcements, and study guides for GATE, SBI Clerk, Bank PO, NISM, and more." />
        <meta name="keywords" content="exam preparation blog, GATE tips, SBI Clerk guide, Bank PO preparation, NISM study guide" />
        <link rel="canonical" href="https://prepkiya.com/blog" />
        <meta property="og:title" content="PrepKiya Blog — Exam Preparation Tips & Guides" />
        <meta property="og:description" content="Exam preparation tips, announcements, and study guides for Indian competitive exams." />
        <meta property="og:url" content="https://prepkiya.com/blog" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      <div className="min-h-screen w-full flex flex-col bg-white text-slate-900 antialiased selection:bg-[#c8933b]/20 selection:text-slate-900">
        <LandingNavbar user={auth?.user} />

        <main className="w-full flex-1 flex flex-col">

          {/* Hero Section */}
          <section className="w-full pt-10 pb-10 sm:pt-14 sm:pb-14 bg-white border-b border-slate-100">
            <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#f4f7fb] border border-slate-200 text-[#131b2e] text-xs font-bold tracking-wider uppercase shadow-2xs mb-3">
                <Rss className="w-3.5 h-3.5 text-[#c8933b]" />
                <span>PrepKiya Editorial & Insights</span>
              </div>

              {/* Headline */}
              <h1 className="!font-sans text-3xl sm:text-4xl lg:text-[42px] font-black text-[#131b2e] tracking-tight leading-[1.15]">
                Insights for Serious Aspirants
              </h1>

              {/* Subtitle */}
              <p className="!font-sans text-sm sm:text-base text-slate-600 leading-relaxed font-normal max-w-2xl mx-auto mt-3">
                Proven preparation strategies, syllabus breakdowns, and verified notification alerts for GATE, Banking, NISM, and government exams.
              </p>

              {/* Search Bar */}
              <div className="relative max-w-xl mx-auto mt-6">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search articles, topics or exams (e.g. GATE, SBI, Tips)..."
                  className="w-full pl-11 pr-10 py-2.5 sm:py-3 rounded-full border border-slate-200 bg-[#f8fafc] text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-[#131b2e] focus:ring-1 focus:ring-[#131b2e] shadow-2xs transition-all"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Category Filter Pills */}
              <div className="mt-6 flex items-center justify-start sm:justify-center gap-2 overflow-x-auto whitespace-nowrap pb-1 no-scrollbar">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => handleCategoryChange(cat)}
                    className={`!font-sans px-4 py-2 rounded-full text-xs sm:text-sm font-bold transition-all cursor-pointer shrink-0 ${
                      selectedCategory === cat
                        ? 'bg-[#131b2e] text-white shadow-xs'
                        : 'bg-[#f8fafc] border border-slate-200 text-slate-700 hover:text-[#131b2e] hover:border-slate-300'
                    }`}
                  >
                    {cat === 'all' ? `All Posts (${pagination.total_count})` : CATEGORY_LABELS[cat]}
                  </button>
                ))}
              </div>

            </div>
          </section>

          {/* Main Content Area */}
          <section className="w-full py-10 sm:py-14 bg-[#f8fafc]">
            <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

              {filteredPosts.length === 0 ? (
                /* Empty state */
                <div className="bg-white border border-slate-200 rounded-2xl p-10 sm:p-14 text-center max-w-lg mx-auto shadow-2xs">
                  <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-4">
                    <BookOpen className="w-7 h-7" />
                  </div>
                  <h3 className="!font-sans text-lg font-bold text-[#131b2e] mb-1">
                    {searchQuery ? 'No articles match your search' : 'No articles published yet'}
                  </h3>
                  <p className="!font-sans text-sm text-slate-500 mb-6">
                    {searchQuery
                      ? `We couldn't find any results for "${searchQuery}". Try another keyword or clear the search.`
                      : 'Our editorial team is writing new study guides and updates. Please check back soon!'}
                  </p>
                  {searchQuery ? (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#131b2e] text-white text-xs font-bold hover:bg-[#1e293b] transition-colors"
                    >
                      <X className="w-3.5 h-3.5" /> Clear Search
                    </button>
                  ) : (
                    <Link
                      href="/"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#131b2e] text-white text-xs font-bold hover:bg-[#1e293b] transition-colors"
                    >
                      Back to Home
                    </Link>
                  )}
                </div>
              ) : (
                <div>

                  {/* Header row above articles */}
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-3 border-b border-slate-200">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-[#131b2e]">
                        {selectedCategory === 'all'
                          ? `All Articles (${filteredPosts.length})`
                          : `${CATEGORY_LABELS[selectedCategory]} (${filteredPosts.length})`}
                      </span>
                      {searchQuery && (
                        <span className="text-xs text-slate-500">
                          matching "{searchQuery}"
                        </span>
                      )}
                    </div>

                    {/* Quick Exam Tag filter badges if available */}
                    {allExamTags.length > 0 && (
                      <div className="flex items-center gap-1.5 overflow-x-auto text-xs text-slate-500">
                        <span className="text-slate-400 font-medium">Quick tag:</span>
                        {allExamTags.slice(0, 5).map((tag) => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => setSearchQuery(searchQuery === tag ? '' : tag)}
                            className={`px-2 py-0.5 rounded text-[11px] font-semibold transition-colors ${
                              searchQuery.toLowerCase() === tag.toLowerCase()
                                ? 'bg-[#131b2e] text-white'
                                : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                            }`}
                          >
                            #{tag}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Scalable Article Grid:
                      - 1 post: centered max-w-lg card
                      - 2 posts: balanced 2-column grid
                      - 3+ posts: clean 3-column grid
                  */}
                  <div
                    className={
                      filteredPosts.length === 1
                        ? 'max-w-lg mx-auto'
                        : filteredPosts.length === 2
                        ? 'max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6'
                        : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
                    }
                  >
                    {filteredPosts.map((post) => {
                      const badge = CATEGORY_BADGES[post.category] || CATEGORY_BADGES.general
                      return (
                        <Link
                          key={post.id}
                          href={`/blog/${post.slug}`}
                          className="group flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                        >
                          {/* Card Header:
                              If cover_image_url exists -> render clean h-44 image
                              If NO image -> compact, elegant h-20 header with category icon & PrepKiya tag
                          */}
                          {post.cover_image_url ? (
                            <div className="w-full h-44 overflow-hidden bg-slate-100 border-b border-slate-100">
                              <img
                                src={post.cover_image_url}
                                alt={post.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                          ) : (
                            <div className="w-full h-20 bg-[#131b2e] px-5 flex items-center justify-between border-b border-slate-800 shrink-0">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-amber-400 group-hover:scale-105 transition-transform">
                                  {getCategoryIcon(post.category)}
                                </div>
                                <span className="text-xs font-bold text-slate-200 uppercase tracking-wide">
                                  {CATEGORY_LABELS[post.category] || post.category}
                                </span>
                              </div>
                              <span className="text-[11px] font-semibold text-slate-400">PrepKiya</span>
                            </div>
                          )}

                          {/* Card Body */}
                          <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
                            <div>
                              {/* Category & Read Time */}
                              <div className="flex items-center justify-between mb-2">
                                <span className={`!font-sans inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${badge.bg} ${badge.text} ${badge.border}`}>
                                  {CATEGORY_LABELS[post.category] || post.category}
                                </span>
                                <span className="!font-sans flex items-center gap-1 text-[11px] text-slate-500 font-medium">
                                  <Clock className="w-3 h-3 text-slate-400" />
                                  {post.read_minutes} min read
                                </span>
                              </div>

                              {/* Title */}
                              <h2 className="!font-sans text-lg font-bold text-[#131b2e] leading-snug group-hover:text-[#c8933b] transition-colors line-clamp-2">
                                {post.title}
                              </h2>

                              {/* Excerpt */}
                              <p className="!font-sans text-sm text-slate-600 leading-relaxed line-clamp-2 mt-2">
                                {post.excerpt}
                              </p>

                              {/* Exam Tags */}
                              {post.exam_tags.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-3 pt-1">
                                  {post.exam_tags.map((tag) => (
                                    <span
                                      key={tag}
                                      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 bg-slate-50 border border-slate-200 rounded-md text-slate-700"
                                    >
                                      <Tag className="w-2.5 h-2.5 text-slate-400" />
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Card Footer */}
                            <div className="flex items-center justify-between pt-3 border-t border-slate-100 mt-2">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-[#131b2e] text-white flex items-center justify-center text-[10px] font-bold">
                                  {post.author_name.charAt(0)}
                                </div>
                                <div className="!font-sans text-xs text-slate-500">
                                  <span className="font-semibold text-slate-700">{post.author_name}</span>
                                  {post.published_at && <span className="text-slate-400"> · {post.published_at}</span>}
                                </div>
                              </div>
                              <span className="!font-sans inline-flex items-center gap-1 text-xs font-bold text-[#131b2e] group-hover:text-[#c8933b] transition-colors">
                                Read <ArrowRight className="w-3.5 h-3.5" />
                              </span>
                            </div>
                          </div>
                        </Link>
                      )
                    })}
                  </div>

                </div>
              )}

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12">
                  {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((p) => (
                    <a
                      key={p}
                      href={`/blog?page=${p}${filters.category ? `&category=${filters.category}` : ''}`}
                      className={`!font-sans w-9 h-9 flex items-center justify-center rounded-lg text-sm font-semibold transition-colors ${
                        p === pagination.current_page
                          ? 'bg-[#131b2e] text-white'
                          : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-400'
                      }`}
                    >
                      {p}
                    </a>
                  ))}
                </div>
              )}

            </div>
          </section>

          {/* Quick Study Banner at Bottom */}
          <section className="w-full py-12 bg-white border-t border-slate-200">
            <div className="w-full max-w-4xl mx-auto px-4 text-center">
              <div className="inline-flex p-3 rounded-2xl bg-amber-50 text-amber-700 border border-amber-200 mb-4">
                <Target className="w-6 h-6 text-[#c8933b]" />
              </div>
              <h3 className="!font-sans text-2xl font-black text-[#131b2e]">
                Ready to Test What You've Learned?
              </h3>
              <p className="!font-sans text-slate-600 text-sm sm:text-base max-w-xl mx-auto mt-2 mb-6">
                Practice with real exam-grade mock tests, topic quizzes, and detailed analytics designed to skyrocket your rank.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link
                  href="/#exams"
                  className="px-5 py-2.5 rounded-xl bg-[#131b2e] text-white text-xs sm:text-sm font-bold hover:bg-[#1e293b] transition-colors shadow-2xs"
                >
                  Explore Exam Mocks
                </Link>
                <Link
                  href="/users/sign_up"
                  className="px-5 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 text-xs sm:text-sm font-bold hover:border-slate-400 transition-colors"
                >
                  Create Free Account
                </Link>
              </div>
            </div>
          </section>

        </main>

        <LandingFooter />
      </div>
    </>
  )
}

